/**
 * AssessmentStartScreen
 *
 * Presents the user with two assessment paths:
 * 1. Anonymous — start immediately, no saved progress or history
 * 2. Authenticated — sign in first, progress is saved, reports are viewable later
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardCheck, LogIn, UserPlus } from "lucide-react";

interface AssessmentStartScreenProps {
  onStartAnonymous: () => void;
}

export const AssessmentStartScreen: React.FC<AssessmentStartScreenProps> = ({
  onStartAnonymous,
}) => {
  const navigate = useNavigate();
  const redirectPath = encodeURIComponent(
    "/tools/readiness-assessment?mode=assessment",
  );

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-10">
        <p className="text-lg text-slate-600 max-w-2xl mx-auto">
          Choose how you'd like to take the assessment. You can proceed
          anonymously or sign in to save your progress and access your results
          later.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Anonymous Path */}
        <div className="bg-white rounded-2xl border border-slate-200 p-8 flex flex-col shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
              <ClipboardCheck className="w-6 h-6 text-slate-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900">
              Take It Anonymously
            </h3>
          </div>

          <p className="text-slate-600 mb-6 flex-1">
            Jump straight into the assessment with no account required. Your
            results will be shown at the end but won't be saved for future
            reference.
          </p>

          <ul className="text-sm text-slate-500 space-y-2 mb-8">
            <li className="flex items-start gap-2">
              <span className="text-green-500 mt-0.5">&#10003;</span>
              No sign-up required
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500 mt-0.5">&#10003;</span>
              See your results immediately
            </li>
            <li className="flex items-start gap-2">
              <span className="text-slate-400 mt-0.5">&#10007;</span>
              Progress is not saved
            </li>
            <li className="flex items-start gap-2">
              <span className="text-slate-400 mt-0.5">&#10007;</span>
              Cannot return to view reports
            </li>
          </ul>

          <button
            onClick={onStartAnonymous}
            className="w-full py-3 px-6 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 transition-colors"
          >
            Start Assessment
          </button>
        </div>

        {/* Authenticated Path */}
        <div className="bg-white rounded-2xl border-2 border-[#B87333] p-8 flex flex-col shadow-sm hover:shadow-md transition-shadow relative">
          <div className="absolute -top-3 right-6 bg-[#B87333] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
            Recommended
          </div>

          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center">
              <LogIn className="w-6 h-6 text-[#B87333]" />
            </div>
            <h3 className="text-xl font-bold text-slate-900">
              Sign In & Save Progress
            </h3>
          </div>

          <p className="text-slate-600 mb-6 flex-1">
            Create an account or sign in to save your assessment progress. Come
            back anytime to view your results and detailed reports.
          </p>

          <ul className="text-sm text-slate-500 space-y-2 mb-8">
            <li className="flex items-start gap-2">
              <span className="text-green-500 mt-0.5">&#10003;</span>
              Progress is automatically saved
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500 mt-0.5">&#10003;</span>
              Access your reports anytime
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500 mt-0.5">&#10003;</span>
              Track improvements over time
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500 mt-0.5">&#10003;</span>
              Get personalized recommendations
            </li>
          </ul>

          <button
            onClick={() => navigate(`/login?redirect=${redirectPath}`)}
            className="w-full py-3 px-6 rounded-xl bg-[#B87333] text-white font-semibold hover:bg-[#A0622D] transition-colors"
          >
            Sign In to Continue
          </button>

          <p className="text-center text-sm text-slate-500 mt-4">
            Don't have an account?{" "}
            <button
              onClick={() => navigate(`/signup?redirect=${redirectPath}`)}
              className="text-[#B87333] hover:underline font-medium inline-flex items-center gap-1"
            >
              <UserPlus className="w-3.5 h-3.5" />
              Sign up
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};
